import axios from 'axios';
import http from 'config/http';

import { ClientModel, ClientLoginImageModel, IAnnouncementBannerModel } from 'api/models';
import { getApiUrl } from 'config/api-url';

export const getAppApiUrl = async (domain: string) => {
    const { data } = await axios.post(`${import.meta.env.VITE_APP_API_URL}/api-url`, {
        domain: domain,
    });

    return data as { url: string };
};

export const getClientLogo = async (domain: string) => {
    const { data } = await axios.post(`${import.meta.env.VITE_APP_API_URL}/auth/client-logo`, {
        domain,
    });

    return data as { logoUrl: string };
};

export const getClientSignInLogo = async (domain: string) => {
    const { data } = await axios.post(`${import.meta.env.VITE_APP_API_URL}/auth/sign-in/logo`, {
        domain,
    });

    return data as { signInLogoUrl: string };
};

export const getAllLoginImages = async (domain: string) => {
    try {
        const { data } = await axios.post(`${getApiUrl()}/auth/login-images`, { domain });

        return data as string[];
    } catch (ex) {
        return null;
    }
};

export const getClient = async () => {
    const { data } = await http.get(`${getApiUrl()}/account/organisation`);

    return ClientModel.fromObject(data);
};

export const createClientDomainRequest = async (payload: { domain: string }) => {
    await http.post(`${getApiUrl()}/account/client-domain-requests`, payload);
};

export const uploadClientLoginImage = async (
    payload: FormData,
    config: { signal?: AbortSignal; onUploadProgress?: (progress: number) => void }
) => {
    const { data } = await http.post(`${getApiUrl()}/account/organisation/preferences/login-images`, payload, {
        signal: config.signal,
        onUploadProgress: (progressEvent) => {
            const progress = (progressEvent.loaded / progressEvent.total) * 100;

            config?.onUploadProgress?.(Math.trunc(progress));
        },
    });

    return ClientLoginImageModel.fromObject(data);
};

export const updateClient = async (payload: Pick<ClientModel, 'name'>) => {
    await http.put(`${getApiUrl()}/account/organisation`, payload);

    // return ClientModel.fromObject(data);
};

export const allowSocialSharingClient = async (allowSocialSharing: boolean) => {
    await http.put(`${getApiUrl()}/account/organisation/preferences/social-sharing`, {
        allowSocialSharing,
    });
};

export const updateClientLogo = async (
    payload: FormData,
    config: { onUploadProgress?: (progress: number) => void }
) => {
    const { data } = await http.put(`${getApiUrl()}/account/organisation/logo`, payload, {
        onUploadProgress: (progressEvent) => {
            const progress = (progressEvent.loaded / progressEvent.total) * 100;

            config?.onUploadProgress?.(Math.trunc(progress));
        },
    });

    return { logoUrl: data.logoUrl } as Pick<ClientModel, 'logoUrl'>;
};

export const updateClientSignInLogo = async (
    payload: FormData,
    config: { onUploadProgress?: (progress: number) => void }
) => {
    const { data } = await http.put(`${getApiUrl()}/account/sign-in/logo`, payload, {
        onUploadProgress: (progressEvent) => {
            const progress = (progressEvent.loaded / progressEvent.total) * 100;

            config?.onUploadProgress?.(Math.trunc(progress));
        },
    });

    return { signInLogoUrl: data.signInLogoUrl } as Pick<ClientModel, 'signInLogoUrl'>;
};

export const updateClientEmailHeaderLogo = async (
    payload: FormData,
    config: { onUploadProgress?: (progress: number) => void }
) => {
    const { data } = await http.put(`${getApiUrl()}/account/organisation/email-header-logo`, payload, {
        onUploadProgress: (progressEvent) => {
            const progress = (progressEvent.loaded / progressEvent.total) * 100;

            config?.onUploadProgress?.(Math.trunc(progress));
        },
    });

    return { emailHeaderLogoUrl: data.emailHeaderLogoUrl } as Pick<ClientModel, 'emailHeaderLogoUrl'>;
};

export const updateClientPrimaryColor = async (primaryColor: string) => {
    await http.put(`${getApiUrl()}/account/organisation/preferences/primary-color`, {
        primaryColor,
    });
};

export const sortClientLoginImages = async (sortOrder: { [x: string]: number }) => {
    await http.put(`${getApiUrl()}/account/organisation/preferences/login-images/sort`, {
        sortOrder,
    });
};

export const removeClientLogo = async () => {
    await http.delete(`${getApiUrl()}/account/organisation/logo`);
};

export const removeClientSignInLogo = async () => {
    await http.delete(`${getApiUrl()}/account/sign-in/logo`);
};

export const removeClientEmailHeaderLogo = async () => {
    await http.delete(`${getApiUrl()}/account/organisation/email-header-logo`);
};

export const deleteClientLoginImage = async (imageId: string) => {
    await http.delete(`${getApiUrl()}/account/organisation/preferences/login-images/${imageId}`);
};

export const getAiTokens = async () => {
    const { data } = await http.get(`${getApiUrl()}/ai-tokens`);

    return data as {
        totalAiTokens: number;
        totalAiTokensUsed: number;
    };
};

export const getAnnouncementBanner = async () => {
    const { data } = await http.get(`${getApiUrl()}/announcement-banners`);

    return data as IAnnouncementBannerModel;
};
